import type React from "react";

import { Trash2, GripVertical } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "~/atoms/button";
import { Input } from "~/atoms/input";
import { StatusBadge } from "~/molecules/WorkflowStatusBadge";
import { cn } from "~/lib/utils";
import { type WorkflowStepStatus } from "~/api/types";

type StepHeaderProps = {
  title: string;
  index: number;
  status: WorkflowStepStatus;
  isEditMode: boolean;
  isEditable: boolean;
  onTitleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemove: () => void;
  error?: string;
};

export function StepHeader({
  title,
  index,
  status,
  isEditMode,
  isEditable,
  onTitleChange,
  onRemove,
  error,
}: StepHeaderProps) {
  const { t } = useTranslation();

  return (
    <div className="border-border/30 bg-secondary/30 flex flex-col gap-2 border-b p-2">
      <div className="flex flex-row items-center gap-2">
        {isEditMode && (
          <div
            className={cn("rounded-full p-1", {
              "bg-primary/10 text-primary/70 cursor-move": isEditable,
              "bg-secondary/70 cursor-not-allowed": !isEditable,
            })}
          >
            <GripVertical className="h-5 w-5" />
          </div>
        )}

        {isEditMode && status !== "completed" ? (
          <Input
            value={title}
            onChange={onTitleChange}
            placeholder={`Step ${index + 1}`}
            className={cn({ "border-red-500 focus:border-red-500": error })}
          />
        ) : (
          <h3 className="font-medium">{title}</h3>
        )}

        <div className="ml-auto flex items-center gap-2">
          <StatusBadge status={status} />

          {isEditMode && (
            <Button
              variant="ghost"
              size="sm"
              className="bg-red-50 text-red-700"
              onClick={onRemove}
              disabled={!isEditable}
            >
              <Trash2 className="h-4 w-4" />
              <span className="sr-only">{t("commons.workflow.delete")}</span>
            </Button>
          )}
        </div>
      </div>

      {error && <div className="pl-8 text-xs text-red-500">{error}</div>}
    </div>
  );
}
